import { useApi } from 'api/useApi';
import { ENV_VARS } from 'app/config/environment-vars';

import type { ProposalChangesResponse } from './proposalChanges/types';
import type { CreateProposalPayload, Proposal, ProposalStatus } from './types';

const PROPOSAL_ERROR_KEY = 'DataApiClient';

type CreateProposalResponse = Pick<Proposal, 'id'>;

export const useProposalApi = () => {
  const baseURL = `${ENV_VARS.DATA_URL}/api`;
  const fetchFunction = useApi(baseURL, PROPOSAL_ERROR_KEY);

  const create = (projectId: string, payload: CreateProposalPayload) => {
    return fetchFunction<CreateProposalResponse>(`/projects/${projectId}/proposals`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  };

  const getProposal = (projectId: string, proposalId: string) => {
    return fetchFunction<Proposal>(`/projects/${projectId}/proposals/${proposalId}`);
  };

  const getAllProposals = (projectId: string, statuses: ProposalStatus[] = []) => {
    const statusQuery = statuses.length > 0 ? `?${statuses.map((status) => `statuses=${status}`).join('&')}` : '';
    return fetchFunction<Proposal[]>(`/projects/${projectId}/proposals${statusQuery}`);
  };

  const discardProposal = (projectId: string, proposalId: string) => {
    return fetchFunction(`/projects/${projectId}/proposals/${proposalId}/changes`, {
      method: 'DELETE'
    });
  };

  const mergeProposal = (projectId: string, proposalId: string) => {
    return fetchFunction(`/projects/${projectId}/proposals/${proposalId}/changes`, {
      method: 'PUT'
    });
  };

  const getProposalChanges = (projectId: string, proposalId: string) => {
    return fetchFunction<ProposalChangesResponse>(`/projects/${projectId}/proposals/${proposalId}/changes`);
  };

  const getEligibleMergers = (projectId: string, proposalId: string) => {
    return fetchFunction<{ users: string[] }>(`/projects/${projectId}/proposals/${proposalId}/changes/eligibility`);
  };

  return {
    create,
    getProposal,
    getAllProposals,
    discardProposal,
    mergeProposal,
    getProposalChanges,
    getEligibleMergers
  };
};
